/**
 * OnboardingPage — Velkomst og introduksjon for kandidatportalen
 *
 * Vises automatisk ved første innlogging (når seen_onboarding=false), eller
 * tilgjengelig manuelt via Hjelp-meny eller når lommeboken er tom.
 *
 * Design: Kompetanseforbundet fargevalør (kf-blue, kf-orange) + Lucide-ikoner.
 * Mobile-first, responsivt grid.
 */

import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Award, GraduationCap, Shield, BookOpen, Sparkles, AlertTriangle,
  CheckCircle2, ArrowRight, FileSignature, Lightbulb, ExternalLink,
  Trash2, Wallet
} from 'lucide-react';

interface Profile {
  seen_onboarding?: boolean;
  full_name?: string;
}

interface Props {
  /** Hvis bruker kommer fra automatisk redirect ved innlogging */
  isFirstTime?: boolean;
  /** Antall attester/vitnemål — påvirker hvilke begrensninger som vises */
  certificateCount?: number;
}

export function OnboardingPage({ isFirstTime = false, certificateCount = 0 }: Props) {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [marking, setMarking] = useState(false);

  useEffect(() => {
    fetch('/api/candidate/profile', { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => d?.success && setProfile(d.profile))
      .catch(() => { /* svelg — ikke kritisk */ });
  }, []);

  async function handleContinue() {
    setMarking(true);
    try {
      await fetch('/api/candidate/profile/seen-onboarding', {
        method: 'PATCH',
        credentials: 'include'
      });
    } catch { /* svelg */ }
    navigate('/dashboard');
  }

  const firstName = profile?.full_name?.split(' ')[0] || '';
  const hasNothing = certificateCount === 0;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 md:py-10 space-y-8">

      {/* HERO */}
      <div className="bg-gradient-to-br from-kf-blue-950 to-kf-blue-700 text-white rounded-2xl p-8 md:p-12 shadow-lg">
        <div className="flex items-start gap-4 mb-4">
          <div className="bg-white/10 backdrop-blur p-3 rounded-xl">
            <Wallet className="w-8 h-8 md:w-10 md:h-10 text-kf-orange-400" />
          </div>
          <div>
            <h1 className="text-2xl md:text-4xl font-bold mb-2 leading-tight">
              {firstName ? `Velkommen, ${firstName}` : 'Velkommen til din kompetanselommebok'}
            </h1>
            <p className="text-kf-blue-100 text-base md:text-lg leading-relaxed max-w-2xl">
              Ditt private bibliotek av kompetanser — kompetanseattester, vitnemål fra
              høyere utdanning, og dokumentasjon på læring du kan dele med arbeidsgivere
              eller bruke i søknader om opptak til videre utdanning.
            </p>
          </div>
        </div>
      </div>

      {/* HVA ER KOMPETANSEATTEST + REALKOMPETANSE */}
      <div className="grid md:grid-cols-2 gap-5">
        <section className="bg-white border border-kf-blue-100 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-start gap-3 mb-3">
            <div className="bg-kf-blue-50 p-2.5 rounded-lg flex-shrink-0">
              <FileSignature className="w-6 h-6 text-kf-blue-600" />
            </div>
            <h2 className="text-lg font-semibold text-kf-blue-900 leading-tight pt-1">
              Hva er en kompetanseattest?
            </h2>
          </div>
          <p className="text-gray-700 leading-relaxed text-sm">
            Et kvalitetssikret bevis på fullført opplæring, utstedt av studieforbundene
            i Kunnskapsdepartementet. Attesten dokumenterer læringsutbytter, EQF/NKR-nivå
            og er digitalt signert. Den kan verifiseres uavhengig og åpner for automatisk
            anerkjennelse i andre systemer.
          </p>
        </section>

        <section className="bg-white border border-kf-blue-100 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-start gap-3 mb-3">
            <div className="bg-kf-orange-50 p-2.5 rounded-lg flex-shrink-0">
              <GraduationCap className="w-6 h-6 text-kf-orange-600" />
            </div>
            <h2 className="text-lg font-semibold text-kf-blue-900 leading-tight pt-1">
              Realkompetansevurdering
            </h2>
          </div>
          <p className="text-gray-700 leading-relaxed text-sm">
            Mange høgskoler og fagskoler aksepterer kompetanseattester og vitnemål
            som grunnlag for opptak via realkompetanse. Du kan dokumentere både
            formell og uformell læring sammen, samlet på ett sted.
          </p>
        </section>
      </div>

      {/* GDPR */}
      <section className="bg-white border border-kf-blue-100 rounded-xl p-6 shadow-sm">
        <div className="flex items-start gap-3 mb-3">
          <div className="bg-kf-success-50 p-2.5 rounded-lg flex-shrink-0">
            <Shield className="w-6 h-6 text-kf-success-700" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-kf-blue-900 leading-tight pt-1">
              Personvern (GDPR)
            </h2>
          </div>
        </div>
        <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
          <p>
            Dataene dine er <strong>kryptert i hvile</strong> og kun synlige for deg.
            Vi lagrer kun det vi trenger:
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 list-none ml-1">
            <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-kf-success-600 mt-0.5 flex-shrink-0" /> Telefonnummer fra Vipps (kryptert)</li>
            <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-kf-success-600 mt-0.5 flex-shrink-0" /> Navn og fødselsdato fra Vipps</li>
            <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-kf-success-600 mt-0.5 flex-shrink-0" /> Attester du selv legger inn</li>
            <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-kf-success-600 mt-0.5 flex-shrink-0" /> Vitnemål du importerer fra VP</li>
          </ul>
          <p className="pt-2 flex items-start gap-2 text-gray-600">
            <Trash2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
            Du kan når som helst slette hele profilen og alle data fra
            <Link to="/settings" className="text-kf-blue-600 hover:underline font-medium ml-1">
              Innstillinger → Slett profil
            </Link>.
          </p>
        </div>
      </section>

      {/* CTA-er */}
      <div className="grid md:grid-cols-2 gap-5">
        <Link
          to="/dashboard"
          className="group bg-white border-2 border-kf-blue-200 hover:border-kf-blue-500 rounded-xl p-6 shadow-sm hover:shadow-md transition-all"
        >
          <div className="flex items-start gap-3 mb-3">
            <div className="bg-kf-blue-50 p-2.5 rounded-lg flex-shrink-0 group-hover:bg-kf-blue-100 transition-colors">
              <GraduationCap className="w-6 h-6 text-kf-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-kf-blue-900 leading-tight pt-1">
              Hent vitnemål fra Vitnemålsportalen
            </h3>
          </div>
          <p className="text-sm text-gray-700 mb-3">
            Importer dine eksisterende vitnemål fra norske universiteter og høgskoler
            med Feide-innlogging.
          </p>
          <span className="text-sm font-medium text-kf-blue-600 inline-flex items-center gap-1 group-hover:text-kf-blue-700">
            Til lommeboken <ArrowRight className="w-4 h-4" />
          </span>
        </Link>

        <a
          href="https://www.kompetanseheving.no"
          target="_blank"
          rel="noopener noreferrer"
          className="group bg-white border-2 border-kf-orange-200 hover:border-kf-orange-500 rounded-xl p-6 shadow-sm hover:shadow-md transition-all"
        >
          <div className="flex items-start gap-3 mb-3">
            <div className="bg-kf-orange-50 p-2.5 rounded-lg flex-shrink-0 group-hover:bg-kf-orange-100 transition-colors">
              <BookOpen className="w-6 h-6 text-kf-orange-600" />
            </div>
            <h3 className="text-lg font-semibold text-kf-blue-900 leading-tight pt-1">
              Bla i kurs hos våre medlemsorganisasjoner
            </h3>
          </div>
          <p className="text-sm text-gray-700 mb-3">
            Kurs hos AOF, Folkeuniversitetet, Studieforbundet næring og samfunn,
            og andre medlemmer under studieforbundene i Kunnskapsdepartementet.
          </p>
          <span className="text-sm font-medium text-kf-orange-700 inline-flex items-center gap-1 group-hover:text-kf-orange-800">
            Til kompetanseheving.no <ExternalLink className="w-4 h-4" />
          </span>
        </a>
      </div>

      {/* DISCLAIMER */}
      <div className="bg-kf-warning-50 border border-kf-warning-200 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-kf-warning-700 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-kf-warning-900 leading-relaxed">
            <strong>Viktig:</strong> Ikke alle tilbydere eller kurs gir utstedelse av
            kompetanseattester. Sjekk med kursleverandør ved påmelding hvis dette
            er viktig for deg.
          </p>
        </div>
      </div>

      {/* HVA HAR DU TILGANG TIL */}
      <section className="bg-white border border-kf-blue-100 rounded-xl p-6 shadow-sm">
        <div className="flex items-start gap-3 mb-4">
          <div className="bg-kf-blue-50 p-2.5 rounded-lg flex-shrink-0">
            <Sparkles className="w-6 h-6 text-kf-blue-600" />
          </div>
          <h2 className="text-lg font-semibold text-kf-blue-900 leading-tight pt-1">
            Hva får du tilgang til?
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <div className="flex items-start gap-2 p-3 bg-kf-blue-50/50 rounded-lg">
            <Award className="w-5 h-5 text-kf-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <strong className="block text-kf-blue-900">CV-bygger</strong>
              <span className="text-gray-700">Profesjonell CV med dine verifiserte ferdigheter</span>
            </div>
          </div>
          <div className="flex items-start gap-2 p-3 bg-kf-blue-50/50 rounded-lg">
            <Lightbulb className="w-5 h-5 text-kf-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <strong className="block text-kf-blue-900">Læringssti</strong>
              <span className="text-gray-700">Personlige kursforslag basert på det du allerede kan</span>
            </div>
          </div>
          <div className="flex items-start gap-2 p-3 bg-kf-blue-50/50 rounded-lg">
            <Wallet className="w-5 h-5 text-kf-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <strong className="block text-kf-blue-900">Digital lommebok</strong>
              <span className="text-gray-700">Apple og Google Wallet-støtte</span>
            </div>
          </div>
          <div className="flex items-start gap-2 p-3 bg-kf-blue-50/50 rounded-lg">
            <Shield className="w-5 h-5 text-kf-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <strong className="block text-kf-blue-900">Sikker deling</strong>
              <span className="text-gray-700">Verifikasjonslenke til arbeidsgivere</span>
            </div>
          </div>
        </div>

        {hasNothing && (
          <div className="mt-5 p-4 bg-kf-warning-50 border border-kf-warning-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-kf-warning-700 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-kf-warning-900 leading-relaxed">
                <strong>Tom lommebok?</strong> CV-bygger og Læringssti fungerer med
                begrensninger til du har lagt inn dokumentasjon. Start med å importere
                fra Vitnemålsportalen eller meld deg på et kurs hos en medlemsorganisasjon.
              </p>
            </div>
          </div>
        )}
      </section>

      {/* CTA: Forstått, fortsett */}
      <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
        <button
          type="button"
          onClick={handleContinue}
          disabled={marking}
          className="bg-kf-blue-600 hover:bg-kf-blue-700 text-white font-medium px-6 py-3 rounded-lg shadow-md hover:shadow-lg transition-all disabled:opacity-50 inline-flex items-center justify-center gap-2"
        >
          {marking ? 'Lagrer...' : (
            <>Forstått, fortsett til lommeboken <ArrowRight className="w-4 h-4" /></>
          )}
        </button>
        {!isFirstTime && (
          <Link
            to="/dashboard"
            className="text-gray-600 hover:text-gray-900 font-medium px-6 py-3 inline-flex items-center justify-center gap-2"
          >
            Hopp over
          </Link>
        )}
      </div>

      <p className="text-center text-xs text-gray-500 pt-4">
        Du kan se denne siden igjen via menyen "Hjelp" når som helst.
      </p>
    </div>
  );
}
